import { memo, useCallback, useMemo, useState } from 'react';

import { Link, useNavigate, useLocation } from 'react-router-dom';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material/styles';
import SvgIcon from '@mui/material/SvgIcon';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';

import { PageContextProps, useApp } from '../../contextProviders/AppProvider';
import { useAuth } from '../../contextProviders/AuthProvider';
import { useData } from '../../contextProviders/DataProvider';
import { useOnlineStatus } from '../../utils/useOnlineStatus';

import GratiIcon from './GratiIcon';
import GratiSettingsMenu from './GratiSettingsMenu';
import SaveStatusIndicator from './SaveStatusIndicator';

interface GratiAppBarProps {
  sx?: SxProps<Theme>;
  isTopLevelPage?: boolean;
}

const NavMenu = memo(
  ({
    anchorElNav,
    eligiblePages,
    handleCloseNavMenu,
  }: {
    anchorElNav: HTMLElement | null;
    eligiblePages: PageContextProps[];
    handleCloseNavMenu: () => void;
  }) => {
    if (eligiblePages.length === 0) return null;

    return (
      <Menu
        id="menu-appbar"
        anchorEl={anchorElNav}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={Boolean(anchorElNav)}
        onClose={handleCloseNavMenu}
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        {eligiblePages.map((page) => (
          <MenuItem component={Link} key={page.name} to={page.to} onClick={handleCloseNavMenu}>
            <Typography variant="labelLarge" sx={{
              textAlign: "center"
            }}>
              {page.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    );
  }
);

NavMenu.displayName = 'NavMenu';

const GratiAppBar = memo((props: GratiAppBarProps) => {
  const { sx, isTopLevelPage } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const { actions, pageActions: appPageActions, getPageTitle, isHomePage, pageContexts } = useApp();
  const visibleActions = actions.length > 0 ? actions : appPageActions;
  const { isGod } = useAuth();
  const { getAdminOrgs, getMemberOrgs, getAllActiveOrgs, userState } = useData();
  const { isEmulator } = useOnlineStatus();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const isMenuItemDisplayed = useCallback(
    (page: PageContextProps) => {
      return (
        (page.showLogin &&
          userState.status === 'ready' &&
          ((page.showMembers && getMemberOrgs().length > 0) ||
            (page.showAdminPro && getAdminOrgs().length > 0) ||
            (page.showGod && isGod)) &&
          (page.showIfOneOrg || getAllActiveOrgs().length > 1)) ||
        (page.showLogout && userState.status !== 'ready')
      );
    },
    [userState.status, getAdminOrgs, getMemberOrgs, getAllActiveOrgs, isGod]
  );

  const eligiblePages = useMemo(() => pageContexts.filter(isMenuItemDisplayed), [pageContexts, isMenuItemDisplayed]);

  const isNavMenuDisplayed = useCallback(() => {
    return isTopLevelPage && eligiblePages.length > 0;
  }, [isTopLevelPage, eligiblePages.length]);

  const handleOpenNavMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  }, []);

  const handleCloseNavMenu = useCallback(() => {
    setAnchorElNav(null);
  }, []);

  const handleBackClick = useCallback(() => {
      if (isTopLevelPage) {
        navigate('/');
      } else if (location.state?.fromNavRail) {
        navigate('/');
      } else if (location.key === 'default') {
        // No React Router history (direct URL entry) — navigate home instead of exiting the app
        navigate('/');
      } else {
        navigate(-1);
      }
  }, [isTopLevelPage, navigate, location.state, location.key]);

  return (
    <AppBar
      position="fixed"
      className={isEmulator ? 'emulator-mode' : ''}
      sx={[
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      <Toolbar disableGutters sx={{ boxShadow: isGod ? (theme: Theme) => `inset 0 0 0 .5rem ${theme.palette.god.main}` : 'none' }}>
        {/* Menu or Back Button */}
        {(eligiblePages.length > 0 && isNavMenuDisplayed()) || !isTopLevelPage ? (
          <Box className="NavMenu" sx={{ flexGrow: isNavMenuDisplayed() ? 1 : 0, display: { xs: 'flex' } }}>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={isNavMenuDisplayed() ? handleOpenNavMenu : handleBackClick}
              edge="start"
              className="AppBarIcon"
              sx={{ display: { xs: 'flex', md: isHomePage ? 'none' : 'flex' } }}
            >
              {isTopLevelPage ? <MenuIcon /> : <ArrowBackIcon />}
            </IconButton>
            <NavMenu anchorElNav={anchorElNav} eligiblePages={eligiblePages} handleCloseNavMenu={handleCloseNavMenu} />
          </Box>
        ) : userState.status === 'ready' && (
          <Box sx={{ flexGrow: 1 }} />
        )
        }

        {/* Title */}
        <Stack
          direction="row"
          className="Headline"
          sx={{
            alignItems: "center",
            ...(isTopLevelPage ? {
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
            } : {
              flex: '1 1 0',
              minWidth: 0,
              overflow: 'hidden',
            })
          }}>
          {isTopLevelPage && (
              <IconButton className="AppBarIcon">
                <GratiIcon className="AppBarIcon" />
              </IconButton>
          )}
            <Typography
              variant={isHomePage ? 'brandMedium' : 'titleMedium'}
              component={"span"}
              sx={{
                display: { xs: 'flex' },
                flexGrow: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {getPageTitle()}
            </Typography>
          <SaveStatusIndicator />
        </Stack>

        <Box sx={{ flexGrow: isTopLevelPage ? 1 : 0 }} />

        {/* Right side items */}
        <Stack direction="row" sx={{
          alignItems: "center"
        }}>
          {visibleActions.map((action) =>
            action.variant && action.variant !== 'icon' ? (
              <Button
                key={action.name}
                onClick={action.handleClick}
                variant={action.variant}
                size="small"
                type={action.type ?? 'button'}
                form={action.form}
                disabled={action.disabled}
                color={action.color}
                sx={{ alignItems: 'center', ml: 1 }}
                startIcon={action.iconElement ?? (action.icon && <SvgIcon component={action.icon} />)}
              >
                {action.name}
              </Button>
            ) : (
              <IconButton
                key={action.name}
                onClick={action.handleClick}
                className="AppBarIcon"
                disabled={action.disabled}
                sx={{ alignItems: 'center' }}
              >
                {action.iconElement ?? (action.icon && <SvgIcon component={action.icon} />)}
              </IconButton>
            )
          )}
        </Stack>
        {userState.status === 'ready' && <GratiSettingsMenu sx={{ flexGrow: 0 }} />}
      </Toolbar>
    </AppBar>
  );
});

GratiAppBar.displayName = 'GratiAppBar';

export default GratiAppBar;
